import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDMiniDrawer from '../../../../../../../BaseComponents/Drawers/MiniDrawer/EDMiniDrawer';
import {
  SidePanels,
  ILocalAnalys,
  IFilledLocalAnalys,
} from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
  ISelectedAnalysAnalys,
} from '../../EkonomiskAnalysPage';
import {
  IColumn,
  ISumData,
} from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';
import { EkonomiskAnalysSideMenuProvider } from './Context/EkonomiskAnalysSideMenuContext';
import { AvenyCollectionMap } from '../../../../../../../Model/ModelContext/ModelContext';
import { ISelection } from '../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import { IUserInfo } from '../../Utils/ekonomiskAnalysInitQueryUtils';
import EkonomiskAnalysSideMenuToolbar from './Components/Toolbar/EkonomiskAnalysSideMenuToolbar';
import { equals } from 'ramda';
import EkonomiskAnalysSideMenuList from './EkonomiskAnalysSideMenuList/EkonomiskAnalysSideMenuList';
import EkonomiskAnalysGrundurvalQuery from './Components/CreateNewAnalys/EkonomiskAnalysGrundurvalQuery/EkonomiskAnalysGrundurvalQuery';
import { EkonomiskAnalysContextConsumer } from '../Context/EkonomiskAnalysContext';
import AvailableColumnTemplatesHandler from './Components/PeriodOchKolumner/Queries/AvailableColumnTemplatesHandler';
import AvailableSumCategoriesQuery from './EkonomiskAnalysSummeringSelect/Queries/AvailableSumCategoriesQuery';
import { ActiveDatabaseConsumer } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import ChangeAnalysFavoriteMutation from './EkonomiskAnalysSelect/Components/FavoriteAnalysDialog/Queries/ChangeAnalysFavoriteMutation/ChangeAnalysFavoriteMutation';
import SaveAnalysMutation from './Components/SaveAnalys/Mutations/SaveAnalysMutation/SaveAnalysMutation';
import DeleteAnalysMutation from './Components/SaveAnalys/Mutations/DeleteAnalysMutation/DeleteAnalysMutation';
import EkonomiskAnalysSaveAnalysView from './Components/SaveAnalys/EkonomiskAnalysSaveAnalysView';
import {
  BudgetSummationModeType,
  SummationModeType,
  defaultSummationMode,
} from '../../Utils/SummationModeUtils';

interface IProps {
  sideMenuIsOpen: boolean;
  openSideMenu: (panel?: SidePanels, callback?: () => void) => () => void;
  closeSideMenu: () => void;
  onAnalysSelected: (
    analys: ISelectedAnalysAnalys,
    codePrivilege: ISelectedAnalysCodePrivilege,
    sammanfattningsMode: boolean,
    newlyCreatedAnalys?: boolean
  ) => void;
  handleExpansion: (panel: SidePanels, callback?: () => void) => () => void;
  onSumsChanged: (sums: ISumData[], urval: IUIUrval) => void;
  onUrvalChanged: (urval: IUIUrval) => void;
  onColumnsChanged: (columns: IColumn[]) => void;
  getPeriodsComponent: (
    dense?: boolean,
    shouldUpdateDirect?: boolean,
    simple?: boolean
  ) => React.ReactNode;
  onDenominationChange: (denomination: number) => void;
  selectedAnalys: ISelectedAnalys | null;
  columnsExpanded: boolean;
  activeDatabaseName: string;
  startAnalysId?: string | null;
  newAnalysSettings: ILocalAnalys;
  currentAnalysSettings: IFilledLocalAnalys;
  urvalOchSummeringExpanded: boolean;
  codeSystemId: string;
  otherExpanded: boolean;
  nrDecimals: number;
  denomination: number;
  decimalsChanged: (decimals: number) => void;
  showComparison: boolean;
  toggleShowComparison: () => void;
  toggleEndastBokfört: () => void;
  onExpandedSumIndexChange: (expandedLevel: number) => void;
  summeringChanged: boolean;
  urvalChanged: boolean;
  ovrigaValChanged: boolean;
  periodKolumnerChanged: boolean;
  resetAnalysSettings: () => void;
  settingsChangedFromOriginal: boolean;
  periodLocked: boolean;
  expandedSumIndex: number;
  collectionMap: AvenyCollectionMap;
  availableReports: ISelection[];
  createNewAnalys: (
    analys: ILocalAnalys,
    selectedAnalys: ISelectedAnalys
  ) => void;
  unsavedAnalys: boolean;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  createNewModalOpen: boolean;
  openCreateNewModal: () => void;
  closeCreateNewModal: () => void;
  isLopandeAnalys: boolean;
  userInfo?: IUserInfo;
  fromDateRef: React.RefObject<HTMLInputElement | null>;
  toDateRef: React.RefObject<HTMLInputElement | null>;
  onBudgetSummationModeChanged: (
    summationmode: BudgetSummationModeType
  ) => void;
  onSummationModeChanged: (summationMode: SummationModeType) => void;
  sideMenuPinned: boolean;
  toggleSideMenuPinned: () => void;
  onExkluderaPersonalkostnaderChanged: (exkludera: boolean) => void;
  updateReport: () => void;
  onVisaSummeringarChanged: (show: boolean) => void;
  disableAll?: boolean;
  classes?: any;
  sammanfattningsMode: boolean;
  isInDetaljanalysTab: boolean;
}

interface IState {
  analysSelectOpen: boolean;
}

const styles = (theme: Theme) => ({
  menuPadding: {
    paddingLeft: 40,
    paddingRight: 40,
  },
  subMenuPadding: {
    paddingLeft: 64,
    paddingRight: 40,
  },
  checkboxTopSpacing: {
    paddingTop: 16,
  },
  drawerSubContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const DISABLE_ALL_BUTTON_TOOLTIP = 'Menyfunktioner avstängda';

/**
 * Sidomeny med inställningar till ekonomisk analys
 */
export class EkonomiskAnalysSideMenu extends React.Component<IProps, IState> {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  analysSelectListRef: React.RefObject<HTMLDivElement | null>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      analysSelectOpen: props.selectedAnalys ? false : true,
    };

    this.scrollContainerRef = React.createRef<HTMLDivElement>();
    this.analysSelectListRef = React.createRef<HTMLDivElement>();
  }

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.sideMenuIsOpen && !this.props.sideMenuIsOpen) {
      this.setState({ analysSelectOpen: false });
    }
    if (prevProps.selectedAnalys && !this.props.selectedAnalys) {
      this.setState({ analysSelectOpen: true });
    }
  }
  setAnalysSelectOpen = (newValue: boolean) => {
    this.setState({ analysSelectOpen: newValue });
  };

  resetAnalysSettings = () => {
    this.props.resetAnalysSettings();
  };

  openSideMenu = (panel?: SidePanels) => () => {
    this.props.openSideMenu(panel)();
  };

  toggleOpenColumns = () => {
    this.props.handleExpansion(
      SidePanels.columns,
      this.focusDateField(!this.props.columnsExpanded)
    )();
  };

  openSideMenuColumns = () => {
    this.props.openSideMenu(SidePanels.columns, this.focusDateField(true))();
  };

  focusDateField = (didOpen: boolean) => () => {
    if (didOpen) {
      const focusToDate =
        this.props.newAnalysSettings.isInterval &&
        this.props.newAnalysSettings.dates.to.userString !== '';
      if (!focusToDate && this.props.fromDateRef.current) {
        this.props.fromDateRef.current.focus();
      } else if (focusToDate && this.props.toDateRef.current) {
        this.props.toDateRef.current.focus();
      }
    }
  };

  isDefaultSummationMode = (summationMode: SummationModeType) =>
    equals(summationMode, defaultSummationMode);

  render() {
    const selectedAnalys = this.props.selectedAnalys;
    return (
      <EkonomiskAnalysSideMenuProvider
        value={{
          newAnalysSettings: this.props.newAnalysSettings,
          setUrval: this.props.onUrvalChanged,
          onSumsChanged: this.props.onSumsChanged,
          onColumnsChanged: this.props.onColumnsChanged,
          getPeriodsComponent: this.props.getPeriodsComponent,
          denomination: this.props.denomination,
          onDenominationChange: this.props.onDenominationChange,
          nrDecimals: this.props.nrDecimals,
          decimalsChanged: this.props.decimalsChanged,
          showComparison: this.props.showComparison,
          toggleShowComparison: this.props.toggleShowComparison,
          changeIncludeEndastBokfört: this.props.toggleEndastBokfört,
          selectedAnalys: this.props.selectedAnalys,
          codeSystemId: this.props.codeSystemId,
          onChipSelectionChanged: this.props.onExpandedSumIndexChange,
          expandedSumLevel: this.props.expandedSumIndex,
          scrollContainerRef: this.scrollContainerRef,
          analysSelectListRef: this.analysSelectListRef,
          analysSelectOpen: this.state.analysSelectOpen,
          periodLocked: this.props.periodLocked,
          createNewAnalys: this.props.createNewAnalys,
          isLopandeAnalys: this.props.isLopandeAnalys,
          userInfo: this.props.userInfo,
          onBudgetSummationModeChanged: this.props.onBudgetSummationModeChanged,
          onSummationModeChanged: this.props.onSummationModeChanged,
          onExkluderaPersonalkostnaderChanged:
            this.props.onExkluderaPersonalkostnaderChanged,
          onVisaSummeringarChanged: this.props.onVisaSummeringarChanged,
        }}
      >
        <EDMiniDrawer
          open={this.props.sideMenuIsOpen && !this.props.disableAll}
          toolbar={
            <EkonomiskAnalysSideMenuToolbar
              onCloseButtonClick={this.props.closeSideMenu}
              onOpenButtonClick={this.openSideMenu(undefined)}
              onResetButtonClick={this.resetAnalysSettings}
              open={this.props.sideMenuIsOpen}
              unsavedAnalys={this.props.unsavedAnalys}
              enabled={
                this.props.impersonatedCodePrivilege !== undefined ||
                !this.isDefaultSummationMode(
                  this.props.newAnalysSettings.other.summationMode
                ) ||
                this.props.settingsChangedFromOriginal
              }
              disabledReason={
                this.props.disableAll ? DISABLE_ALL_BUTTON_TOOLTIP : undefined
              }
              sideMenuOpen={this.props.sideMenuIsOpen && !this.props.disableAll}
              pinned={this.props.sideMenuPinned}
              togglePinned={this.props.toggleSideMenuPinned}
              forceDisable={this.props.disableAll}
            />
          }
          close={this.props.closeSideMenu}
          hideToolbarDivider={!this.props.sideMenuIsOpen}
        >
          <EkonomiskAnalysGrundurvalQuery>
            {grundurval => {
              const helaHuvudboken = grundurval.find(
                x => x.name === 'Hela huvudboken'
              );
              return (
                <EkonomiskAnalysContextConsumer>
                  {({ updateReport }) => (
                    <AvailableColumnTemplatesHandler>
                      {availableColumnTemplates => (
                        <AvailableSumCategoriesQuery>
                          {({ availableCategories }) => (
                            <>
                              {availableCategories && (
                                <div
                                  className={
                                    this.props.classes.drawerSubContainer
                                  }
                                >
                                  <EkonomiskAnalysSideMenuList
                                    activeDatabaseName={
                                      this.props.activeDatabaseName
                                    }
                                    analysSelectOpen={
                                      this.state.analysSelectOpen
                                    }
                                    availableCategories={availableCategories}
                                    availableColumnTemplates={
                                      availableColumnTemplates
                                    }
                                    availableReports={
                                      this.props.availableReports
                                    }
                                    closeCreateNewModal={
                                      this.props.closeCreateNewModal
                                    }
                                    collectionMap={this.props.collectionMap}
                                    columnsExpanded={this.props.columnsExpanded}
                                    createNewModalOpen={
                                      this.props.createNewModalOpen
                                    }
                                    currentAnalysSettings={
                                      this.props.currentAnalysSettings
                                    }
                                    handleExpansion={this.props.handleExpansion}
                                    helaHuvudboken={helaHuvudboken}
                                    isLopandeAnalys={this.props.isLopandeAnalys}
                                    newAnalysSettings={
                                      this.props.newAnalysSettings
                                    }
                                    onAnalysSelected={
                                      this.props.onAnalysSelected
                                    }
                                    openCreateNewModal={
                                      this.props.openCreateNewModal
                                    }
                                    openSideMenu={this.openSideMenu}
                                    openSideMenuColumns={
                                      this.openSideMenuColumns
                                    }
                                    otherExpanded={this.props.otherExpanded}
                                    ovrigaValChanged={
                                      this.props.ovrigaValChanged
                                    }
                                    periodKolumnerChanged={
                                      this.props.periodKolumnerChanged
                                    }
                                    scrollContainerRef={this.scrollContainerRef}
                                    selectedAnalys={selectedAnalys}
                                    setAnalysSelectOpen={
                                      this.setAnalysSelectOpen
                                    }
                                    settingsChangedFromOriginal={
                                      this.props.settingsChangedFromOriginal
                                    }
                                    sideMenuIsOpen={this.props.sideMenuIsOpen}
                                    summeringChanged={
                                      this.props.summeringChanged
                                    }
                                    toggleOpenColumns={this.toggleOpenColumns}
                                    unsavedAnalys={this.props.unsavedAnalys}
                                    updateReport={updateReport}
                                    urvalChanged={this.props.urvalChanged}
                                    urvalOchSummeringExpanded={
                                      this.props.urvalOchSummeringExpanded
                                    }
                                    userInfo={this.props.userInfo}
                                    sammanfattningsMode={
                                      this.props.sammanfattningsMode
                                    }
                                    isInDetaljanalysTab={
                                      this.props.isInDetaljanalysTab
                                    }
                                  />
                                  {selectedAnalys && (
                                    <ActiveDatabaseConsumer>
                                      {({ activeDatabase }) =>
                                        activeDatabase && (
                                          <ChangeAnalysFavoriteMutation>
                                            {changeFavoriteAnalys => (
                                              <SaveAnalysMutation>
                                                {saveAnalys => (
                                                  <DeleteAnalysMutation
                                                    currentStartAnalys={
                                                      this.props.userInfo
                                                        ? this.props.userInfo
                                                            .startAnalys
                                                        : undefined
                                                    }
                                                    deselectCurrentAnalys
                                                  >
                                                    {deleteAnalys => (
                                                      <EkonomiskAnalysSaveAnalysView
                                                        sideMenuOpen={
                                                          this.props
                                                            .sideMenuIsOpen
                                                        }
                                                        openSideMenu={this.openSideMenu()}
                                                        settingsChanged={
                                                          this.props
                                                            .settingsChangedFromOriginal
                                                        }
                                                        onAnalysSelected={(
                                                          analys,
                                                          codePrivilege,
                                                          sammanfattningsMode
                                                        ) =>
                                                          this.props.onAnalysSelected(
                                                            analys,
                                                            codePrivilege,
                                                            sammanfattningsMode,
                                                            true
                                                          )
                                                        }
                                                        analysSettings={
                                                          this.props
                                                            .newAnalysSettings
                                                        }
                                                        selectedAnalys={
                                                          selectedAnalys
                                                        }
                                                        dbName={activeDatabase}
                                                        changeFavoriteAnalys={
                                                          changeFavoriteAnalys
                                                        }
                                                        saveAnalys={saveAnalys}
                                                        updateReport={
                                                          updateReport
                                                        }
                                                        selectedAnalysNewlyCreated={
                                                          this.props
                                                            .unsavedAnalys
                                                        }
                                                        userInfo={
                                                          this.props.userInfo
                                                        }
                                                        deleteAnalys={deleteAnalys(
                                                          {
                                                            id: selectedAnalys
                                                              .analys.id,
                                                          },
                                                          activeDatabase
                                                        )}
                                                        disabled={
                                                          this.props.disableAll
                                                        }
                                                        sammanfattningsMode={
                                                          this.props
                                                            .sammanfattningsMode
                                                        }
                                                      />
                                                    )}
                                                  </DeleteAnalysMutation>
                                                )}
                                              </SaveAnalysMutation>
                                            )}
                                          </ChangeAnalysFavoriteMutation>
                                        )
                                      }
                                    </ActiveDatabaseConsumer>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </AvailableSumCategoriesQuery>
                      )}
                    </AvailableColumnTemplatesHandler>
                  )}
                </EkonomiskAnalysContextConsumer>
              );
            }}
          </EkonomiskAnalysGrundurvalQuery>
        </EDMiniDrawer>
      </EkonomiskAnalysSideMenuProvider>
    );
  }
}

export default withStyles(EkonomiskAnalysSideMenu, styles);
